import React, { useState, useEffect } from "react";
import axiosInstance from "../../services/auth";
import ReactPaginate from "react-paginate";
import { toast, ToastContainer } from "react-toastify";
import LoanForm from "./LoanForm";
import LoanView from "./LoanView";
import { useCombinedContext } from "../../context/CombinedContext";
import exportToExcel from "../Excel/ExportToExcel";
import LoanRepayment from "./LoanRepayment";
import ViewReport from "./ViewReport";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { Link } from "react-router-dom";
import LoanReport from "./LoanReport";
import LoanPaymentReport from "./LoanPaymentReport";
import LoanExcelReport from "./LoanExcelReport";

const LoanTable = () => {
  const [loan, setLoan] = useState([]);
  const [employee, setEmployee] = useState([]);

  const [fromDate, setFromDate] = useState(""); // Start date for filtering
  const [toDate, setToDate] = useState(""); // End date for filtering
  const [selectedData, setSelectedData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showExcelReport, setShowExcelReport] = useState(false);
  const [showPaymentReport, setShowPaymentReport] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [isViewMode, setIsViewMode] = useState(false);
  const [selectedYear, setSelectedYear] = useState(""); // For year-wise filtering
  const [loading, setLoading] = useState(false);
  const { selectesEmployee, selectedCompany } = useCombinedContext();
  const companyId = selectedCompany?._id;
  const companyCode = selectedCompany?.company_code;
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [searchText, setSearchText] = useState("");
  const [loanStatusFilter, setLoanStatusFilter] = useState("All");
  const [monthPaidFilter, setMonthPaidFilter] = useState("All");

  // New state for status confirmation modal
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [statusAction, setStatusAction] = useState(null);
  const [statusLoan, setStatusLoan] = useState(null);

  // console.log(loan, "Loan");
  const fetchLoan = async () => {
    try {
      setLoading(true);
      const params = {};
      if (fromDate) params.start_date = fromDate;
      if (toDate) params.end_date = toDate;

      const response = await axiosInstance.get(
        `/loan/report/new/${companyId}`,
        {
          params,
        },
      );

      const sortedData = (response.data.data || []).sort((b, a) => {
        const nameA = a.employee?.name?.toLowerCase() || "";
        const nameB = b.employee?.name?.toLowerCase() || "";
        return nameA.localeCompare(nameB);
      });

      setLoan(sortedData);
      if (selectedData) {
        const updated = sortedData.find(
          (item) => item.employee?._id === selectedData.employee?._id,
        );
        if (updated) setSelectedData(updated);
      }
    } catch (error) {
      toast.error("Error fetching loan");
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployee = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/loan/employee/${companyId}`);
      setEmployee(response.data.employees || []);
    } catch (error) {
      toast.error("Error fetching Employee");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchLoan();
    }
  }, [companyId, fromDate, toDate]);

  const handleSearchChange = (e) => {
    setSearchText(e.target.value.toLowerCase());
    setCurrentPage(0);
  };

  // Get data from selected year
  const handleYearChange = (e) => {
    const year = e.target.value;
    setSelectedYear(year);
    if (year) {
      setFromDate(`${year}-01-01`);
      setToDate(`${year}-12-31`);
    } else {
      setFromDate("");
      setToDate("");
    }
  };

  const handlePageClick = (selectedPage) => {
    setCurrentPage(selectedPage.selected);
  };

  // Sum of repayments made in the current calendar month
  const getThisMonthPaid = (item) => {
    const now = new Date();
    return (item.repaymentTransactions || [])
      .filter((tx) => {
        const txDate = new Date(tx.date);
        return (
          txDate.getMonth() === now.getMonth() &&
          txDate.getFullYear() === now.getFullYear()
        );
      })
      .reduce((sum, tx) => sum + (Number(tx.amount) || 0), 0);
  };

  // Derived filtering: search text + loan status (balance) + this month paid
  const filteredLoans = loan
    .filter((item) => {
      const name = item.employee?.name?.toString().toLowerCase() || "";
      const empNo = item.employee?.employeeNo?.toString().toLowerCase() || "";
      const appNo = item.applicationNo?.toString().toLowerCase() || "";
      return (
        name.includes(searchText) ||
        empNo.includes(searchText) ||
        appNo.includes(searchText)
      );
    })
    .filter((item) => {
      const balance = Number(item.balanceAmount) || 0;
      if (loanStatusFilter === "Active") return balance > 0;
      if (loanStatusFilter === "Closed") return balance === 0;
      return true;
    })
    .filter((item) => {
      const paid = getThisMonthPaid(item);
      if (monthPaidFilter === "Paid") return paid > 0;
      if (monthPaidFilter === "Not Paid") return paid === 0;
      return true;
    });

  const currentLoanItems = [...filteredLoans]
    .reverse()
    .slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);
  const pageCount = Math.ceil(filteredLoans.length / itemsPerPage);

  // Check Role
  const authData =
    JSON.parse(localStorage.getItem("authData")) ||
    JSON.parse(sessionStorage.getItem("authData"));
  const userRole = authData.role;
  const updateLoanStatus = async (loanId, status) => {
    try {
      await axiosInstance.post(`/loan/loan-approval/${loanId}`, { status });
      toast.success(`Loan ${status.toLowerCase()} successfully`);
      fetchLoan();
    } catch (error) {
      toast.error(`Failed to ${status.toLowerCase()} loan`);
    }
  };
  // New: Handle status change for pending loans (approve/reject) with custom confirmation modal
  const handleStatusChange = (loanItem, action) => {
    if (loanItem.status !== "Pending") {
      toast.info("Only pending loans can be updated.");
      return;
    }
    setStatusLoan(loanItem);
    setStatusAction(action);
    setIsStatusModalOpen(true);
  };
  const confirmStatusChange = async () => {
    await updateLoanStatus(statusLoan._id, statusAction);
    setIsStatusModalOpen(false);
    setStatusLoan(null);
    setStatusAction(null);
  };
  const cancelStatusChange = () => {
    setIsStatusModalOpen(false);
    setStatusLoan(null);
    setStatusAction(null);
  };
  function formatPrice(price) {
    const num = parseFloat(price);
    if (isNaN(num)) return "N/A";
    return num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  const handleExport = () => {
    const sheetData = [];
    const merges = [];

    const currentDate = new Date();
    const reportDate = currentDate.toLocaleDateString(); // or use toISOString().split("T")[0]

    // First row: Report generated date
    sheetData.push([`Report Generated Date: ${reportDate}`]);

    // Merge across all 9 columns
    merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: 8 } });

    // Headers
    const headers = [
      "Sl No",
      "Employee Name",
      "Employee No",
      "Total Loan Amount",
      "Balance Amount",
      "Company Name",
      "Company Code",
      "This Month Repayments",
      "Total Paid Amount",
    ];
    sheetData.push(headers);

    // Subheaders (blank)
    sheetData.push(["", "", "", "", "", "", "", "", ""]);

    // Merge header cells vertically
    for (let col = 0; col < headers.length; col++) {
      merges.push({ s: { r: 1, c: col }, e: { r: 2, c: col } });
    }

    filteredLoans.forEach((item, index) => {
      const thisMonthRepayments = getThisMonthPaid(item);

      const totalPaid = item.repaymentTransactions?.reduce(
        (sum, tx) => sum + (tx.amount || 0),
        0,
      );

      const row = [
        index + 1,
        item.employee?.name || "",
        item.employee?.employeeNo || "",
        item.totalLoanAmount || 0,
        item.balanceAmount || 0,
        item.company?.company_name || "",
        item.company?.company_code || "",
        thisMonthRepayments || 0,
        totalPaid || 0,
      ];

      sheetData.push(row);
    });

    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
    worksheet["!merges"] = merges;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Loan Report");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const dataBlob = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });

    saveAs(dataBlob, "exported_loan_data.xlsx");
  };

  const refreshSelectedData = (employeeId) => {
    const updated = loan.find((item) => item.employee?._id === employeeId);
    if (updated) {
      setSelectedData({ ...updated });
    }
  };

  return (
    <div>
      <ToastContainer />
      <Link to="/loan">
        <button className="linkBtn mb-3">
          <i className="bi bi-arrow-left me-2"></i>Back to Loan
        </button>
      </Link>
      <div className="mb-4 headStyle d-flex  justify-content-between align-items-center">
        <h4 className="mb-0">Loan Report</h4>

        <button
          className="button-2"
          onClick={() => {
            if (selectedCompany?.status === false) {
              toast.error(
                "The selected company is inactive. Cannot add a loan.",
              );
            } else {
              setSelectedData(null);
              setShowForm(true);
            }
          }}
          style={{
            borderRadius: "8px",
            whiteSpace: "nowrap",
            padding: "8px 16px",
          }}
        >
          Add Loan
        </button>
      </div>
      <div
        className="d-flex flex-wrap mt-3 align-items-center gap-3"
        style={{
          padding: "16px",
          boxShadow: "0px 0px 10px 0px #e0e0e0",
          background: "white",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
          }}
        >
          <label htmlFor="fromDate" style={{ width: "-webkit-fill-available" }}>
            From Date
          </label>
          <input
            className="form-control"
            type="date"
            placeholder="From Date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{
              border: "1px solid #1E2A5E",
              borderRadius: "8px",
            }}
          />
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
          }}
        >
          <label htmlFor="toDate" style={{ width: "-webkit-fill-available" }}>
            To Date
          </label>
          <input
            className="form-control"
            type="date"
            placeholder="To Date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{
              border: "1px solid #1E2A5E",
              borderRadius: "8px",
            }}
          />
        </div>
        <div>
          <select
            className="form-select"
            value={selectedYear}
            onChange={handleYearChange}
            style={{
              width: "70px",
              maxHeight: "300px !important",
              overflowY: "auto",
            }}
          >
            <option value="">Year</option>
            {Array.from(
              { length: new Date().getFullYear() - 2000 + 1 },
              (_, i) => 2000 + i,
            ).map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>

        {/* Right Side: Add Loan Button */}
      </div>
      <div className="headStyle mt-4">
        <div className="d-flex mt-2 flex-wrap align-items-center gap-3 justify-content-between">
          <div className="d-flex flex-wrap align-items-center gap-3">
            <div>
              {" "}
              <input
                className="form-control"
                type="search"
                placeholder="Search..."
                style={{
                  maxWidth: "100%",
                  border: "1px solid #1E2A5E",
                  borderRadius: "8px",
                }}
                onChange={handleSearchChange}
                value={searchText}
              />
            </div>

            <div className="d-flex align-items-center">
              <label className="me-2 pt-1">Count</label>
              <select
                className="form-select no-arrow"
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                style={{ width: "auto" }}
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="15">15</option>
                <option value="20">20</option>
                <option value="25">25</option>
                <option value="30">30</option>
              </select>
            </div>
            <div className="d-flex align-items-center">
              <label className="me-2 pt-1">Loan Status</label>
              <select
                className="form-select no-arrow"
                value={loanStatusFilter}
                onChange={(e) => {
                  setLoanStatusFilter(e.target.value);
                  setCurrentPage(0);
                }}
                style={{ width: "auto" }}
              >
                <option value="All">All</option>
                <option value="Active">Active (Balance &gt; 0)</option>
                <option value="Closed">Closed (Balance = 0)</option>
              </select>
            </div>
            <div className="d-flex align-items-center">
              <label className="me-2 pt-1">This Month Paid</label>
              <select
                className="form-select no-arrow"
                value={monthPaidFilter}
                onChange={(e) => {
                  setMonthPaidFilter(e.target.value);
                  setCurrentPage(0);
                }}
                style={{ width: "auto" }}
              >
                <option value="All">All</option>
                <option value="Paid">Paid</option>
                <option value="Not Paid">Not Paid</option>
              </select>
            </div>
          </div>
          {["admin", "superadmin"].includes(userRole) && (
            <div style={{ flex: "0 1 auto" }}>
              <button
                className="button-2 exxportBtn"
                onClick={() => {
                  setShowExcelReport(true);
                }}
              >
                Export to Excel
              </button>
            </div>
          )}
        </div>
        <div
          className="my-3 rounded table-responsive-sm"
          style={{ overflowX: "auto" }}
        >
          <table id="my-table" className="table table-bordered mb-0">
            <thead className="t_head">
              <tr className="text-center align-middle">
                <th className="align-middle">Sl No</th>
                <th className="align-middle">Employee Name</th>
                <th className="align-middle">Employee No</th>
                <th className="align-middle">Loan Amount</th>
                <th className="align-middle">Total Repayment</th>
                <th className="align-middle">Balance Repayment</th>
                <th className="align-middle">This Month Paid</th>
                <th className="align-middle">Repayment</th>
                <th className="align-middle">Report</th>
                {/* <th className="text-center align-middle">Actions</th> */}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="15">
                    <div className="d-flex justify-content-center m-3">
                      <div className="spinner"></div>
                    </div>
                  </td>
                </tr>
              ) : currentLoanItems.length === 0 ? (
                <tr>
                  <td colSpan="15">
                    <div className="d-flex justify-content-center m-1">
                      <h6>Data Not Found</h6>
                    </div>
                  </td>
                </tr>
              ) : (
                currentLoanItems.map((loanItem, index) => {
                  const totalRepayment = loanItem.repayments
                    ? loanItem.repayments.reduce(
                        (acc, cur) => acc + cur.amount,
                        0,
                      )
                    : 0;
                  const balanceRepayment = loanItem.loanAmount - totalRepayment;

                  return (
                    <tr key={loanItem._id || index}>
                      <td>{currentPage * itemsPerPage + index + 1}</td>
                      <td>{loanItem.employee?.name || "N/A"}</td>
                      <td>{loanItem.employee?.employeeNo || "N/A"}</td>

                      <td>
                        $ {formatPrice(loanItem.totalLoanAmount) || "N/A"}
                      </td>
                      <td>
                        $ {formatPrice(loanItem.totalRepaymentAmount) || "N/A"}
                      </td>
                      <td>$ {formatPrice(loanItem.balanceAmount) || "N/A"}</td>
                      <td className="text-center">
                        $ {formatPrice(getThisMonthPaid(loanItem))}
                      </td>

                      <td className="text-center">
                        <button
                          className="button-2 viewBtn"
                          onClick={() => {
                            setSelectedData(loanItem);
                            setShowPaymentReport(true);
                          }}
                        >
                          View
                        </button>
                      </td>
                      <td className="text-center">
                        <button
                          className="button-2 viewReportBtn"
                          onClick={() => {
                            setSelectedData(loanItem);
                            setShowReport(true);
                          }}
                        >
                          View Report
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <ReactPaginate
          breakLabel="..."
          nextLabel="next >"
          onPageChange={handlePageClick}
          pageRangeDisplayed={5}
          pageCount={pageCount}
          previousLabel="< previous"
          renderOnZeroPageCount={null}
          containerClassName="pagination justify-content-center pagination-sm"
          pageClassName="page-item"
          pageLinkClassName="page-link"
          activeClassName="active"
          previousClassName="page-item"
          previousLinkClassName="page-link"
          nextClassName="page-item"
          nextLinkClassName="page-link"
          disabledClassName="disabled"
        />
        {isViewMode && (
          <LoanView
            loan={selectedData}
            isViewMode={isViewMode}
            userRole={userRole}
            formatPrice={formatPrice}
            setIsViewMode={setIsViewMode}
          />
        )}
        {showForm && (
          <LoanForm
            loan={selectedData}
            companyCode={companyCode}
            companyId={companyId}
            employee={employee}
            selectedCompany={selectedCompany}
            onClose={() => {
              setShowForm(false);
              fetchLoan();
            }}
          />
        )}
        {showPaymentForm && (
          <LoanRepayment
            loan={selectedData}
            companyCode={companyCode}
            companyId={companyId}
            formatPrice={formatPrice}
            employee={employee}
            selectedCompany={selectedCompany}
            onClose={() => {
              setShowPaymentForm(false);
              fetchLoan();
            }}
          />
        )}
        {showExcelReport && (
          <LoanExcelReport
            formatPrice={formatPrice}
            companyId={companyId}
            onClose={() => {
              setShowExcelReport(false);
            }}
          />
        )}
        {showPaymentReport && (
          <LoanPaymentReport
            loan={selectedData}
            companyCode={companyCode}
            companyId={companyId}
            formatPrice={formatPrice}
            employee={employee}
            selectedCompany={selectedCompany}
            fetchLoan={fetchLoan}
            onClose={() => {
              setShowPaymentReport(false);
              fetchLoan();
            }}
            fetchLoanData={async () => {
              await fetchLoan(); // this must complete
              const updated = loan.find(
                (item) => item.employee?._id === selectedData.employee?._id,
              );
              if (updated) setSelectedData(updated); // update to trigger re-render
            }}
          />
        )}
        {showReport && (
          <LoanReport
            loan={selectedData}
            companyCode={companyCode}
            companyId={companyId}
            employee={employee}
            formatPrice={formatPrice}
            selectedCompany={selectedCompany}
            onClose={() => {
              setShowReport(false);
              fetchLoan();
            }}
          />
        )}
        {/* Status Confirmation Modal */}
        {isStatusModalOpen && (
          <div
            className={`modal fade ${isStatusModalOpen ? "show d-block" : ""}`}
            style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)" }}
            tabIndex="-1"
            role="dialog"
            aria-labelledby="statusModalLabel"
            aria-hidden={!isStatusModalOpen}
            onClick={(e) => {
              if (e.target.classList.contains("modal")) cancelStatusChange();
            }}
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id="statusModalLabel">
                    Confirm {statusAction} Loan
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={cancelStatusChange}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="modalDiv">
                    Are you sure you want to {statusAction.toLowerCase()} this
                    loan? This action cannot be undone.
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="button-2" onClick={cancelStatusChange}>
                    Cancel
                  </button>
                  <button className="button-2" onClick={confirmStatusChange}>
                    {statusAction}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoanTable;
